import { parseValidDomainKey } from 'common/logic'
import OpenGraph, { ExtraOpenGraph } from 'components/OpenGraph'
import config from 'common/config'
import routes from 'common/routes'
import {
  getDomainMeta,
  getDomainReviewCounts,
  getDomainHashtags,
  getUsersForCollection,
  getMostReviewedDomains,
} from 'utils/hooks'
import TimeAgo from 'cc/TimeAgo'
import { plur, cn, sameDate, inspectObjectHTML } from 'common/utils'
import { Hashtag } from 'cc/Hashtags'
import { EmptyPage, EmptySection } from 'components/Empty'

import { ExternalDomainLink } from 'components/Link'
import { hours, weeks } from 'common/utils/dates'
import { handleFallback } from 'utils/pages'
import DomainLogo from 'components/DomainLogo'
import ReviewCard from 'cc/ReviewCard'
import BoldHeaderStripe from 'components/PageLayouts/BoldHeaderStripe'
import useReviewPageTheme from 'utils/hooks/useReviewPageTheme'
import VoteArrowOverview from 'cc/VoteArrowOverview'
import PageSection from 'cc/PageSection'

import { firestore } from 'lib/server/firebase'

const EmptyDomain = ({ domainKey, invalid }) => (
  <EmptyPage>
    <div>
      No data found on <strong>{domainKey}</strong>
      {invalid && (
        <div className="small">
          (which, btw, doesn't appear to be a valid domain name...)
        </div>
      )}
    </div>
  </EmptyPage>
)

const DomainHashtags = ({ hashtagCounts }) => {
  if (!hashtagCounts?.length) return null
  const tags = hashtagCounts ? hashtagCounts.map(({ id: tag }) => tag) : []

  return (
    <>
      <ExtraOpenGraph prop="article:tag" val={tags} />
      <div className="flex justify-center flex-wrap space-x-3 xl:space-x-5">
        {tags.map((tag) => (
          <Hashtag
            key={tag}
            tag={tag}
            className="text-2xl font-light hover:text-blue-700"
            link
          />
        ))}
      </div>
    </>
  )
}

const Domain = ({
  domainKey,
  unparsedDomainKeyParam,
  domainMeta,

  hashtagCounts,
  reviewCounts,
}) => {
  if (!domainKey)
    return <EmptyDomain domainKey={unparsedDomainKeyParam} invalid />

  const {
    createdAt,
    lastDomainReviewAt,
    numDomainReviews = 0,
    numPageReviews = 0,
    numRuleReviews = 0,
  } = domainMeta

  const headerBg = {
    // moodBgBold: 'bg-conic-to-tl from-gray-900 via-gray-100 to-gray-900',
    moodBgBold: 'bg-gradient-to-bl from-gray-200 via-gray-400 to-gray-600',
  }
  const header = (
    <div className="flex flex-col items-center space-y-5">
      <DomainLogo domainKey={domainKey} size={145} className="rounded" />
      <div className="flex flex-col">
        <h1 className="m-0 font-serif text-3xl text-gray-800 sm:text-4xl md:text-5xl">
          {domainKey}
          <ExternalDomainLink domainKey={domainKey} />
        </h1>
        <div className="text-xs text-gray-500 sm:text-sm md:text-base text-center">
          First reviewed <TimeAgo datetime={createdAt} />.
        </div>
        {numDomainReviews > 0 ? (
          <div className="text-xs text-gray-500 sm:text-sm md:text-base text-center">
            {plur(numDomainReviews, 'domain review')}.
          </div>
        ) : null}
        {numPageReviews > 0 ? (
          <div className="text-xs text-gray-500 sm:text-sm md:text-base text-center">
            {plur(numPageReviews, 'page review')}.
          </div>
        ) : null}
        {numRuleReviews > 0 ? (
          <div className="text-xs text-gray-500 sm:text-sm md:text-base text-center">
            {plur(numRuleReviews, 'rule review')}.
          </div>
        ) : null}
      </div>
    </div>
  )

  return (
    <div className="">
      <OpenGraph
        url={routes.siteUrl(domainKey)}
        title={[domainKey, `Reviews on ${config.appName}`].join(' | ')}
        description={`${domainKey} has ${plur(numDomainReviews, 'review')} on ${
          config.appName
        }.`}
        // image={TODO: pull from our best guess?} // todo: fall back to clearbit?
        type="article"
        article__published_time={createdAt}
        article__modified_time={lastDomainReviewAt}
      />

      <BoldHeaderStripe theme={headerBg} header={header}>
        <div className="flex justify-center my-5 lg:my-10">
          <VoteArrowOverview
            reviewCounts={reviewCounts}
            className="flex p-5 justify-center space-x-5"
          />
        </div>

        {hashtagCounts.length ? (
          <PageSection className="shadow-inner bg-opacity-75">
            <DomainHashtags hashtagCounts={hashtagCounts} />
          </PageSection>
        ) : null}
      </BoldHeaderStripe>
    </div>
  )
}

export async function getStaticPaths() {
  // TODO: expand this? for now just the ones linked from /domains
  const domains = await getMostReviewedDomains()

  const paths = domains.map(({ id: domainKey }) => ({
    params: { domainKey },
  }))

  // TODO: toggle fallback to enable rendering for more than the top reviewed
  return { paths, fallback: false }
}

export async function getStaticProps({ params }) {
  const domainKey = parseValidDomainKey(params.domainKey)
  const domainMeta = await getDomainMeta(domainKey)
  const reviewCounts = await getDomainReviewCounts(domainKey, firestore)
  const hashtagCounts = await getDomainHashtags(domainKey)

  return {
    props: {
      unparsedDomainKeyParam: params.domainKey,
      domainKey,
      domainMeta,
      hashtagCounts,
      reviewCounts,
    },
    revalidate: domainKey ? 1 * hours : weeks,
  }
}

Domain.fullscreen = true
Domain.domainLogos = true

export default handleFallback(Domain, { loadingTitle: 'Loading domain...' })
